/**
 * Referrals Page
 * 
 * Main page for managing referrals with sections:
 * - Invite Block (dominant, referral link)
 * - Goals & Progress
 * - Invited Friends list
 * - Referral Rewards
 * - FAQ
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../lib/api/client'
import { InviteBlock } from './InviteBlock'
import { GoalsBlock } from './GoalsBlock'
import { FriendsListBlock } from './FriendsListBlock'
import { ReferralRewardsBlock } from './ReferralRewardsBlock'
import { ReferralFAQBlock } from './ReferralFAQBlock'
import { Skeleton } from '../ui/skeleton'
import type { ReferralStats, ReferralGoal, UserReward } from '../../types'

export function ReferralsPage() {
  const { t } = useTranslation('common')
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [goals, setGoals] = useState<ReferralGoal[]>([])
  const [rewards, setRewards] = useState<UserReward[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const loadData = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const [statsResult, goalsResult, rewardsResult] = await Promise.all([
        apiClient.referrals.getReferralStats(),
        apiClient.referrals.getGoalsProgress(),
        apiClient.rewards.getUserRewards(),
      ])
      
      setStats(statsResult)
      setGoals(goalsResult.goals)
      // Filter to only show referral-sourced rewards
      setRewards(rewardsResult.rewards.filter((r: UserReward) => r.source_referral_id !== null))
    } catch (err: any) {
      console.error('Error loading referral data:', err)
      setError(err.message || 'Failed to load referral data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [user])

  const handleGoalClaimed = () => {
    // Reload data after claiming a goal
    loadData()
  }

  const handleRewardClaimed = () => {
    // Reload data after claiming a reward
    loadData()
  }

  if (loading) {
    return (
      <div className="referrals-page min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-6 space-y-8">
          <Skeleton className="h-64 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-48 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="referrals-page min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-6">
          <div className="error-state text-center py-12">
            <p className="text-destructive">{error}</p>
            <button 
              onClick={loadData} 
              className="mt-4 text-primary hover:underline"
            >
              {t('common.retry', 'Try again')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="referrals-page min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6 space-y-8">
        {/* Page Header */}
        <div className="page-header flex items-center gap-3 mb-6">
          <div className="w-2 h-8 bg-emerald-500 rounded-full"></div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('referrals.title', 'Referrals')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('referrals.subtitle', 'Invite friends and earn rewards together')}
            </p>
          </div>
        </div>

        {/* Section 1: Invite Block (visually dominant) */}
        <InviteBlock 
          referralCode={stats?.referral_code} 
          lifetimeStats={stats?.lifetime_stats}
        />

        {/* Section 2: Goals & Progress */}
        <GoalsBlock 
          goals={goals} 
          activeReferrals={stats?.active_referrals || 0}
          onGoalClaimed={handleGoalClaimed}
        />

        {/* Section 3: Invited Friends */}
        <FriendsListBlock 
          referrals={stats?.referrals || []}
          totalReferrals={stats?.total_referrals || 0}
          activeReferrals={stats?.active_referrals || 0}
        />

        {/* Section 4: Referral Rewards */}
        <ReferralRewardsBlock 
          rewards={rewards}
          onRewardClaimed={handleRewardClaimed}
        />

        {/* Section 5: FAQ */}
        <ReferralFAQBlock />
      </div>
    </div>
  )
}

